import { Button } from '@mui/material';
import { FunctionComponent, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import AlertsOffIcon from 'assets/alerts-off.svg?react';
import AlertsOnIcon from 'assets/alerts-on.svg?react';
import AqiNotificationsIcon from 'assets/alert-aqi.svg?react';
import AtmosphericNotificationsIcon from 'assets/alert-atomospheric.svg?react';
import WaterLevelNotificationsIcon from 'assets/alert-water-level.svg?react';
import { DownState } from 'components/DownState';
import { useCreateAlertButton } from 'features/weatherAlerts/hooks/useCreateAlertButton';
import { GenericWeatherStation } from 'features/weatherAlerts/weatherAlerts.types';
import { openNativeAppSettings } from 'shared/utils';

type NotificationsDownStateProps = {
  isPushEnabled?: boolean;
  station?: GenericWeatherStation;
};

const iconMap: Record<
  GenericWeatherStation['weatherStationType'],
  FunctionComponent<SVGProps<SVGSVGElement>>
> = {
  aqi: AqiNotificationsIcon,
  atmospheric: AtmosphericNotificationsIcon,
  waterLevel: WaterLevelNotificationsIcon,
};

export const NotificationsDownState = (
  props: NotificationsDownStateProps,
): JSX.Element => {
  const { isPushEnabled = true, station } = props;
  const { t } = useTranslation();
  const createAlertButtonProps = useCreateAlertButton(station);

  const weatherStationType = station?.weatherStationType ?? 'atmospheric';
  const StationIcon = iconMap[weatherStationType];

  return (
    <DownState
      states={[
        {
          criteria: () => !isPushEnabled,
          renderImage: (imageProps) => <AlertsOffIcon {...imageProps} />,
          title: t('downState.turnOn.title'),
          description: t('downState.turnOn.description'),
          Button: (
            <Button fullWidth size="large" onClick={openNativeAppSettings}>
              {t('downState.turnOn.button')}
            </Button>
          ),
          containerProps: { spacing: 6, sx: { p: 2 } },
        },
        {
          criteria: () => Boolean(station),
          renderImage: (imageProps) => <StationIcon {...imageProps} />,
          title: t(`downState.add.${weatherStationType}.title`),
          description: t(`downState.add.${weatherStationType}.description`),
          Button: (
            <Button fullWidth size="large" {...createAlertButtonProps}>
              {t('downState.addNotification')}
            </Button>
          ),
          containerProps: { spacing: 6, sx: { p: 2, pt: 4 } },
        },
        {
          renderImage: (imageProps) => <AlertsOnIcon {...imageProps} />,
          title: t('downState.configure.title'),
          description: t('downState.configure.description'),
          Button: (
            <Button
              component={Link}
              fullWidth
              size="large"
              to="/?menu=layers&section=weather"
            >
              {t('downState.configure.button')}
            </Button>
          ),
          containerProps: { sx: { height: '100%', px: 2, pt: 6 } },
        },
      ]}
    />
  );
};
